<template>
  <div>
    <b-card
      no-body
      class="mb-0"
    >
      <b-card-body class="p-50">
        <div class="d-flex align-items-center">
          <label class="mb-0 mr-50 font-weight-bold text-nowrap">
            Opening Balance:
          </label>
          <b-form-input
            ref="openingBalanceInput"
            v-model.number="todayBalance.amount"
            type="number"
            size="sm"
            placeholder="Enter Amount"
            class="compact-input"
            :disabled="isProcessing"
            :class="{ 'border-danger': todayBalance.amount !== null && todayBalance.amount < 0 }"
            @keydown.enter="saveTodayBalance"
          />
          <feather-icon
            v-if="!isProcessing"
            :icon="todayBalance.id ? 'EditIcon' : 'PlusIcon'"
            size="20"
            class="ml-50 cursor-pointer text-primary"
            :class="{ 'opacity-50': !isValidBalance }"
            @click="isValidBalance && saveTodayBalance()"
          />
          <b-spinner
            v-else
            small
            variant="primary"
            class="ml-50"
          />

          <b-dropdown
            ref="historyDropdown"
            variant="link"
            no-caret
            right
            toggle-class="p-0 text-decoration-none ml-50"
            menu-class="dropdown-menu-custom"
            :no-close-on-select="true"
            @show="dropdownOpen = true"
            @hide="dropdownOpen = false"
          >
            <template #button-content>
              <feather-icon
                :icon="dropdownOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="20"
                class="text-primary"
              />
            </template>

            <div
              class="dropdown-content-wrapper"
              @click.stop
              @mousedown.stop
            >
              <div class="filter-section p-50 w-100">
                <flat-picker
                  v-model="dateRange"
                  placeholder="Select Date Range"
                  :config="flatPickerConfig"
                  class="w-100 d-flex justify-content-center"
                />
              </div>

              <div
                v-if="dateRange"
                class="table-section"
              >
                <table class="table table-sm table-bordered mb-0">
                  <thead>
                    <tr class="bg-light">
                      <th style="width: 10%;">
                        #
                      </th>
                      <th style="width: 25%;">
                        Date
                      </th>
                      <th
                        style="width: 28%;"
                        class="text-right"
                      >
                        Amount (₹)
                      </th>
                      <th style="width: 35%;">
                        Created By
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(record, index) in historyRecords"
                      :key="record.id"
                    >
                      <td>{{ index + 1 }}</td>
                      <td>{{ formatedDate(record.date, 'DD-MM-YYYY') }}</td>
                      <td class="text-right">
                        {{ formatAmount(record.amount) }}
                      </td>
                      <td>{{ record.created_by_name || '-' }}</td>
                    </tr>

                    <tr v-if="historyRecords.length === 0 && !loadingHistory">
                      <td
                        colspan="4"
                        class="text-center text-muted"
                      >
                        No records found
                      </td>
                    </tr>

                    <tr v-if="loadingHistory">
                      <td
                        colspan="4"
                        class="text-center"
                      >
                        <b-spinner small />
                        Loading...
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </b-dropdown>
        </div>

        <b-alert
          v-if="errorMessage"
          variant="danger"
          show
          dismissible
          class="mt-50 py-50"
          @dismissed="errorMessage = null"
        >
          {{ errorMessage }}
        </b-alert>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BFormInput,
  BSpinner,
  BAlert,
  BDropdown,
} from 'bootstrap-vue'
import FlatPicker from '@/components/UI/FlatPicker.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import getParsedQueryString from '@/utils/headerQueryParser'

export default {
  components: {
    BCard,
    BCardBody,
    BFormInput,
    BSpinner,
    BAlert,
    BDropdown,
    FlatPicker,
  },
  mixins: [dateMixin, formatMixin],
  data() {
    return {
      todayBalance: {
        id: null,
        amount: null,
        date: this.formatedDate(new Date(), 'YYYY-MM-DD'),
      },
      dateRange: null,
      historyRecords: [],
      loadingHistory: false,
      isProcessing: false,
      errorMessage: null,
      dropdownOpen: false,
      flatPickerConfig: {
        mode: 'range',
        static: true,
      },
    }
  },
  computed: {
    isValidBalance() {
      return this.todayBalance.amount !== null && this.todayBalance.amount >= 0
    },
  },
  watch: {
    dateRange(newVal) {
      if (newVal) {
        this.fetchHistory()
      }
    },
  },
  mounted() {
    this.fetchTodayBalance()
    this.setDefaultDateRange()
  },
  methods: {
    focusInput() {
      if (this.$refs.openingBalanceInput) {
        this.$refs.openingBalanceInput.focus()
      }
    },
    setDefaultDateRange() {
      const today = new Date()
      const sevenDaysAgo = new Date()
      sevenDaysAgo.setDate(today.getDate() - 7)

      const startDate = this.formatedDate(sevenDaysAgo, 'YYYY-MM-DD')
      const endDate = this.formatedDate(today, 'YYYY-MM-DD')

      this.dateRange = `${startDate} to ${endDate}`
      // Fetch history immediately after setting default range
      this.$nextTick(() => {
        this.fetchHistory()
      })
    },
    async fetchTodayBalance() {
      try {
        const todayStr = this.formatedDate(new Date(), 'YYYY-MM-DD')
        const response = await axios.get(`/operations/opening_balance/?${getParsedQueryString({
          start_date: todayStr,
          end_date: todayStr,
        })}`)
        if (response.data && response.data.results && response.data.results.length > 0) {
          const record = response.data.results[0]
          this.todayBalance = {
            id: record.id,
            amount: record.amount,
            date: record.date,
          }
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to load opening balance'
      }
    },
    async saveTodayBalance() {
      if (!this.isValidBalance || this.isProcessing) return

      this.isProcessing = true
      this.errorMessage = null

      try {
        const formData = {
          amount: this.todayBalance.amount,
        }

        if (this.todayBalance.id) {
          await axios.patch(`/operations/opening_balance/${this.todayBalance.id}/`, formData)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Opening balance updated',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          const response = await axios.post('/operations/opening_balance/', formData)
          this.todayBalance.id = response.data.id
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Opening balance added',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }

        // Refresh history after save
        if (this.dateRange) {
          await this.fetchHistory()
        }

        this.$emit('data-updated')
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to save'
      } finally {
        this.isProcessing = false
      }
    },
    async fetchHistory() {
      if (!this.dateRange) return

      this.loadingHistory = true
      this.errorMessage = null

      try {
        const dates = this.dateRange.split(' to ')
        const params = {}

        if (dates.length === 2) {
          // Date range selected
          params.start_date = dates[0].trim()
          params.end_date = dates[1].trim()
        } else if (dates.length === 1 && dates[0]) {
          // Single date selected
          params.start_date = dates[0].trim()
          params.end_date = dates[0].trim()
        }

        params.per_page = 100

        const response = await axios.get(`/operations/opening_balance/?${getParsedQueryString(params)}`)
        this.historyRecords = response.data.results || []
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to load history'
        this.historyRecords = []
      } finally {
        this.loadingHistory = false
      }
    },
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00'
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
  },
}
</script>

<style scoped>
.compact-input {
  width: 120px;
  flex-shrink: 0;
}

.p-50 {
  padding: 0.4rem !important;
}

.mb-50 {
  margin-bottom: 0.5rem !important;
}

.mt-50 {
  margin-top: 0.5rem !important;
}

.mr-25 {
  margin-right: 0.25rem !important;
}

.mr-50 {
  margin-right: 0.5rem !important;
}

.ml-50 {
  margin-left: 0.5rem !important;
}

.border-danger {
  border-color: #ea5455 !important;
}

.table-sm td,
.table-sm th {
  padding: 0.3rem;
  vertical-align: middle;
  font-size: 0.875rem;
}

.dropdown-content-wrapper {
  padding: 0;
}

.filter-section {
  min-width: 250px;
  border-bottom: 1px solid #857d9d;
}

.table-section {
  max-height: 350px;
  overflow-y: auto;
}

::v-deep .dropdown-menu-custom {
  padding: 0 !important;
  min-width: 350px;
  max-width: 350px;
}

::v-deep .flatpickr-calendar {
  position: relative !important;
  top: 0 !important;
  left: 0 !important;
}

/* Add these new styles for Flatpicker full width */
::v-deep .filter-section .flatpickr-wrapper {
  width: 80% !important;
  display: block !important;
}

::v-deep .filter-section .form-control {
  width: 100% !important;
}

::v-deep .filter-section .flatpickr-input {
  width: 80% !important;
}
</style>
